import FileCard from './FileCard'

/**
 * Componente que gestiona la previsualización detallada de un archivo.
 * * Dependiendo del tipo MIME del archivo, renderiza:
 * - Una imagen con sombra y metadatos.
 * - Un visor de PDF integrado (iframe).
 * - Una vista de descarga para otros tipos de archivos.
 *
 * @component
 * @param {Object} props - Propiedades del componente.
 * @param {Object} props.file - Objeto que representa el archivo.
 * @param {string} props.file.url - URL del recurso.
 * @param {string} props.file.name - Nombre del archivo para mostrar y texto alternativo.
 * @param {string} [props.file.mimeType] - Tipo de medio (ej: 'image/jpeg', 'application/pdf').
 * @param {string|Date} [props.file.createdAt] - Fecha de creación para mostrar en metadatos de imagen.
 * * @returns {JSX.Element|null} El contenido de la previsualización o null si no hay archivo.
 */
const FilePreview = ({ file }) => {
  if (!file) return null
  const { url, name, mimeType, createdAt } = file

  if (mimeType?.startsWith('image/')) {
    return (
      <div className="text-center p-2">
        <img
          src={url}
          alt={name}
          className="img-fluid rounded shadow"
          style={{ maxHeight: '70vh', objectFit: 'contain' }}
        />
        <div className="mt-3 small text-muted-custom">
          {name} • {new Date(createdAt).toLocaleString()}
        </div>
      </div>
    )
  }

  if (mimeType === 'application/pdf') {
    return (
      <iframe
        src={url}
        title={name}
        style={{
          width: '100%',
          height: '75vh',
          border: 'none',
          borderRadius: '8px',
        }}
      />
    )
  }

  return (
    <div className="text-center py-5">
      <i className="fas fa-file-download fa-4x mb-3 text-primary opacity-50"></i>
      <p className="fw-bold">{name}</p>
      <a
        href={url}
        target="_blank"
        rel="noreferrer"
        className="btn btn-primary px-4"
      >
        Descargar Archivo
      </a>
    </div>
  )
}

/**
 * Componente que renderiza una lista horizontal de archivos adjuntos.
 * * Muestra una serie de `FileCard` y maneja la apertura de un modal para la
 * previsualización detallada mediante el componente `FilePreview`. Sin
 * archivos, se reduce a una sola línea discreta.
 *
 * @component
 * @param {Object} props - Propiedades del componente.
 * @param {Array<Object>} props.files - Arreglo de objetos de archivo a mostrar.
 * @param {Function} props.openLargeModal - Función para abrir el modal de previsualización.
 * Recibe (titulo, componente_JSX).
 * * @returns {JSX.Element} Lista de adjuntos con scroll horizontal.
 */
const FileList = ({ files, openLargeModal }) => {
  const isEmpty = files.length === 0

  return (
    <>
      <div className={`fl-head ${isEmpty ? '' : 'mb-2'}`}>
        <h6 className="section-subtitle m-0">Adjuntos</h6>
        {isEmpty ? (
          <span className="fl-empty">Sin adjuntos</span>
        ) : (
          <span className="fl-count">{files.length}</span>
        )}
      </div>

      {!isEmpty && (
        <div className="d-flex gap-2 overflow-auto pb-1 custom-scrollbar">
          {files.map((file, i) => (
            <FileCard
              key={i}
              file={file}
              size="sm"
              onSelect={() =>
                openLargeModal(file.name, <FilePreview file={file} />)
              }
            />
          ))}
        </div>
      )}

      <style>{`
        .fl-head {
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .fl-empty {
          font-size: 0.78rem;
          color: var(--gm-text-faint);
        }
        .fl-count {
          font-size: 0.68rem;
          font-weight: 600;
          padding: 0 7px;
          border-radius: 99px;
          color: var(--gm-text-muted);
          background: var(--gm-surface-2);
        }
      `}</style>
    </>
  )
}

export default FileList
